/**
 * Agent-owned personal mail Connect (Settings → Email → Personal).
 *
 * OAuth start + callback live on the Pages worker (`pages-worker/_worker.js`):
 *   GET  /api/agents/personal-mail/config
 *   GET  /api/agents/personal-mail/oauth/start?provider=&agentId=
 *   GET  /api/agents/personal-mail/oauth/callback
 *   GET  /api/agents/personal-mail/atw/start?agentId=   (session bind)
 *
 * Tokens never go in localStorage. Phase 1 stores only connection metadata on
 * the agent (provider, status, mailbox). Refresh/access tokens stay on the
 * worker (httpOnly cookie when a token secret is set). Durable KV/D1 storage
 * is the next step for send/draft runtime.
 *
 * Secrets (Cloudflare Pages project `atw-agents`):
 *   wrangler pages secret put GOOGLE_CLIENT_ID --project-name=atw-agents
 *   wrangler pages secret put GOOGLE_CLIENT_SECRET --project-name=atw-agents
 *   wrangler pages secret put MICROSOFT_CLIENT_ID --project-name=atw-agents
 *   wrangler pages secret put MICROSOFT_CLIENT_SECRET --project-name=atw-agents
 *   wrangler pages secret put ZOHO_MAIL_CLIENT_ID --project-name=atw-agents
 *   wrangler pages secret put ZOHO_MAIL_CLIENT_SECRET --project-name=atw-agents
 *   wrangler pages secret put PERSONAL_MAIL_STATE_SECRET --project-name=atw-agents
 *   wrangler pages secret put PERSONAL_MAIL_TOKEN_SECRET --project-name=atw-agents
 * Optional: GOOGLE_REDIRECT_URI, MICROSOFT_REDIRECT_URI, ZOHO_MAIL_REDIRECT_URI,
 * MICROSOFT_TENANT (default common), ZOHO_ACCOUNTS_DOMAIN (default accounts.zoho.com).
 * Register redirect URI: https://workforce.atw.net/api/agents/personal-mail/oauth/callback
 * (also https://agents.atw.net/... while the legacy host still serves).
 */

import { agentsHref } from "@/lib/atwAgentsHost";
import { apiUrl } from "@/lib/apiOrigin";

export type AgentMailProvider = "google" | "microsoft" | "atw_mail" | "zoho";

/** Honest connect state for agent-owned personal mail. */
export type PersonalMailStatus = "disconnected" | "pending" | "connected";

/** Subset of agent fields used by personal-mail status / migration. */
export type AgentPersonalMailFields = {
  personalMailProvider?: AgentMailProvider | null;
  personalMailStatus?: PersonalMailStatus;
  personalMailConnected?: boolean;
  mailboxAddress?: string | null;
  mailboxSource?: "existing" | "created" | null;
};

export type AgentPersonalMailProviderOption = {
  id: AgentMailProvider;
  /** Short label shown in the picker. */
  label: string;
  /** One-line description under the label. */
  description: string;
  /**
   * Whether the client can attempt a real connect start (OAuth or ATW session).
   * Live readiness still comes from `/config` (missing client IDs).
   */
  oauthWireReady: boolean;
  /** Short note when OAuth wiring is not live yet. */
  nextStepNote: string;
  /** Button label for start connect. */
  connectButtonLabel: string;
  /** Exact secret names Kyle must set for a live OAuth test. */
  requiredSecrets: string[];
};

export const AGENT_MAIL_PROVIDER_OPTIONS: AgentPersonalMailProviderOption[] = [
  {
    id: "google",
    label: "Google Workspace / Gmail",
    description: "OAuth 2.0 + Gmail API (send, drafts, modify).",
    oauthWireReady: true,
    nextStepNote:
      "Google mail is not available for this workspace yet. Ask an ATW admin if you need Gmail for this agent.",
    connectButtonLabel: "Continue with Google",
    requiredSecrets: ["GOOGLE_CLIENT_ID", "GOOGLE_CLIENT_SECRET"],
  },
  {
    id: "microsoft",
    label: "Microsoft 365 / Outlook",
    description: "OAuth 2.0 + Microsoft Graph mail.",
    oauthWireReady: true,
    nextStepNote:
      "Microsoft mail is not available for this workspace yet. Ask an ATW admin if you need Outlook for this agent.",
    connectButtonLabel: "Continue with Microsoft",
    requiredSecrets: ["MICROSOFT_CLIENT_ID", "MICROSOFT_CLIENT_SECRET"],
  },
  {
    id: "atw_mail",
    label: "ATW Mail",
    description: "ATW’s own mail product. Agent mailbox on ATW.",
    oauthWireReady: true,
    nextStepNote:
      "Uses your signed-in ATW session (auth.atw.net). No separate OAuth client. Sign in on workforce.atw.net first.",
    connectButtonLabel: "Connect with ATW Mail",
    requiredSecrets: [],
  },
  {
    id: "zoho",
    label: "Zoho Mail",
    description: "Zoho OAuth for ATW / Zoho customer mailboxes.",
    oauthWireReady: true,
    nextStepNote:
      "Zoho Mail is not available for this workspace yet. Ask an ATW admin if you need Zoho for this agent.",
    connectButtonLabel: "Continue with Zoho Mail",
    requiredSecrets: ["ZOHO_MAIL_CLIENT_ID", "ZOHO_MAIL_CLIENT_SECRET"],
  },
];

const PROVIDER_IDS = new Set<string>(AGENT_MAIL_PROVIDER_OPTIONS.map((row) => row.id));

/** Legacy Phase 1 ids that must not stay as connected stubs. */
const LEGACY_PROVIDER_ALIASES: Record<string, AgentMailProvider | null> = {
  google: "google",
  microsoft: "microsoft",
  atw_mail: "atw_mail",
  zoho: "zoho",
  imap_smtp: null,
};

export type PersonalMailProviderConfig = {
  ready: boolean;
  /** Legacy field; public /config no longer returns secret names. */
  missing?: string[];
  mode: "oauth" | "session";
  /** Customer-safe message when ready is false. */
  note?: string;
};

export type PersonalMailConfigResponse = {
  ok: boolean;
  callbackUrl: string;
  tokenStorageNote: string;
  providers: Record<AgentMailProvider, PersonalMailProviderConfig>;
};

/** Query keys written by the worker callback / ATW session bind. */
export const PERSONAL_MAIL_CALLBACK_PARAM = "personal_mail";
export const PERSONAL_MAIL_PROVIDER_PARAM = "pm_provider";
export const PERSONAL_MAIL_EMAIL_PARAM = "pm_email";
export const PERSONAL_MAIL_ERROR_PARAM = "pm_error";

export type PersonalMailCallbackResult =
  | {
      kind: "connected";
      provider: AgentMailProvider;
      email: string;
    }
  | {
      kind: "error";
      message: string;
      provider: AgentMailProvider | null;
    }
  | { kind: "none" };

export function isAgentMailProvider(value: unknown): value is AgentMailProvider {
  return typeof value === "string" && PROVIDER_IDS.has(value);
}

export function normalizeAgentMailProvider(value: unknown): AgentMailProvider | null {
  if (typeof value !== "string") return null;
  if (isAgentMailProvider(value)) return value;
  if (value in LEGACY_PROVIDER_ALIASES) return LEGACY_PROVIDER_ALIASES[value] ?? null;
  return null;
}

export function agentMailProviderOption(
  provider: AgentMailProvider | null | undefined,
): AgentPersonalMailProviderOption | undefined {
  if (!provider) return undefined;
  return AGENT_MAIL_PROVIDER_OPTIONS.find((row) => row.id === provider);
}

export function agentMailProviderLabel(
  provider: AgentMailProvider | null | undefined,
): string {
  if (!provider) return "email";
  return agentMailProviderOption(provider)?.label ?? "email";
}

export function personalMailSettingsPath(agentId: string): string {
  return agentsHref(`/agents/${agentId}/settings/email/personal`);
}

/**
 * Where Google (and later providers) must redirect after OAuth.
 * Full absolute URL is built at start time from the current Agents host.
 */
export function personalMailOAuthReturnPath(agentId: string): string {
  return personalMailSettingsPath(agentId);
}

export function personalMailOAuthStartUrl(
  agentId: string,
  provider: AgentMailProvider,
): string {
  if (provider === "atw_mail") {
    const url = new URL(apiUrl("/api/agents/personal-mail/atw/start"));
    url.searchParams.set("agentId", agentId);
    return url.toString();
  }
  const url = new URL(apiUrl("/api/agents/personal-mail/oauth/start"));
  url.searchParams.set("provider", provider);
  url.searchParams.set("agentId", agentId);
  return url.toString();
}

export function personalMailConfigUrl(): string {
  return apiUrl("/api/agents/personal-mail/config");
}

export async function fetchPersonalMailConfig(): Promise<PersonalMailConfigResponse | null> {
  try {
    const res = await fetch(personalMailConfigUrl(), {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as PersonalMailConfigResponse;
  } catch {
    return null;
  }
}

export function parsePersonalMailCallbackSearch(
  search: string,
): PersonalMailCallbackResult {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const status = params.get(PERSONAL_MAIL_CALLBACK_PARAM);
  if (!status) return { kind: "none" };

  const provider = normalizeAgentMailProvider(params.get(PERSONAL_MAIL_PROVIDER_PARAM));
  if (status === "error") {
    const message =
      params.get(PERSONAL_MAIL_ERROR_PARAM)?.trim() ||
      "Personal mail connect failed.";
    return { kind: "error", message, provider };
  }
  if (status === "connected") {
    const email = (params.get(PERSONAL_MAIL_EMAIL_PARAM) || "").trim().toLowerCase();
    if (!provider || !email || !email.includes("@")) {
      return {
        kind: "error",
        message: "OAuth returned without a mailbox identity.",
        provider,
      };
    }
    return { kind: "connected", provider, email };
  }
  return { kind: "none" };
}

/** Strip personal-mail callback query keys from the current URL (keep the path). */
export function stripPersonalMailCallbackParams(search: string): string {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  params.delete(PERSONAL_MAIL_CALLBACK_PARAM);
  params.delete(PERSONAL_MAIL_PROVIDER_PARAM);
  params.delete(PERSONAL_MAIL_EMAIL_PARAM);
  params.delete(PERSONAL_MAIL_ERROR_PARAM);
  const next = params.toString();
  return next ? `?${next}` : "";
}

/**
 * Survives the OAuth round-trip and roster hydration race.
 * The SPA used to apply `?personal_mail=connected` immediately, then D1
 * hydration replaced the agent with the older `pending` row and wiped connect.
 */
const PERSONAL_MAIL_OAUTH_HANDOFF_KEY = "atw_agents_personal_mail_oauth_handoff_v1";

export type PersonalMailOAuthHandoff = {
  agentId: string;
  result: Exclude<PersonalMailCallbackResult, { kind: "none" }>;
  at: number;
};

export function stashPersonalMailOAuthHandoff(
  agentId: string,
  result: Exclude<PersonalMailCallbackResult, { kind: "none" }>,
): void {
  if (typeof window === "undefined") return;
  const id = agentId.trim();
  if (!id) return;
  const payload: PersonalMailOAuthHandoff = {
    agentId: id,
    result,
    at: Date.now(),
  };
  try {
    window.sessionStorage.setItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY, JSON.stringify(payload));
  } catch {
    /* private mode / quota */
  }
}

export function readPersonalMailOAuthHandoff(
  agentId: string,
): PersonalMailOAuthHandoff | null {
  if (typeof window === "undefined") return null;
  const id = agentId.trim();
  if (!id) return null;
  try {
    const raw = window.sessionStorage.getItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersonalMailOAuthHandoff>;
    if (!parsed || parsed.agentId !== id || !parsed.result) return null;
    if (parsed.result.kind !== "connected" && parsed.result.kind !== "error") return null;
    // Drop stale handoffs (OAuth state itself is 15 minutes).
    if (typeof parsed.at === "number" && Date.now() - parsed.at > 30 * 60 * 1000) {
      window.sessionStorage.removeItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY);
      return null;
    }
    return parsed as PersonalMailOAuthHandoff;
  } catch {
    return null;
  }
}

export function clearPersonalMailOAuthHandoff(agentId?: string): void {
  if (typeof window === "undefined") return;
  try {
    if (!agentId) {
      window.sessionStorage.removeItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY);
      return;
    }
    const raw = window.sessionStorage.getItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { agentId?: string };
    if (parsed?.agentId === agentId.trim()) {
      window.sessionStorage.removeItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY);
    }
  } catch {
    try {
      window.sessionStorage.removeItem(PERSONAL_MAIL_OAUTH_HANDOFF_KEY);
    } catch {
      /* ignore */
    }
  }
}

/** Map provider/token errors into short customer-facing copy (no secret names). */
export function personalMailCustomerErrorMessage(raw: string): string {
  const message = raw.trim();
  if (!message) return "Personal mail connect failed.";
  const lower = message.toLowerCase();
  if (
    lower.includes("access_denied") ||
    lower.includes("access denied") ||
    lower.includes("consent_required")
  ) {
    return "Sign-in was cancelled or blocked. This agent was not connected. Try again and accept the mail permissions, or ask an admin if this account needs app approval.";
  }
  if (
    lower.includes("permissions were not granted") ||
    lower.includes("missing refresh token") ||
    lower.includes("offline gmail access") ||
    lower.includes("offline mail access") ||
    lower.includes("offline access")
  ) {
    return message.length > 280 ? `${message.slice(0, 277)}…` : message;
  }
  if (
    lower.includes("unverified") ||
    lower.includes("verification") ||
    lower.includes("app is blocked") ||
    lower.includes("has not completed the google verification")
  ) {
    return "Google blocked this connect because the ATW mail app is still in Google verification for Gmail scopes. An ATW admin must finish verification or add this account as a test user.";
  }
  if (lower.includes("redirect_uri_mismatch")) {
    return "Google rejected the return URL for this workspace. Ask an ATW admin to check the OAuth redirect URI.";
  }
  if (lower.includes("invalid_grant") || lower.includes("expired")) {
    return "The Google sign-in expired before it finished. Start Connect again.";
  }
  return message.length > 280 ? `${message.slice(0, 277)}…` : message;
}

export function resolvePersonalMailStatus(agent: AgentPersonalMailFields): PersonalMailStatus {
  const mailbox = agent.mailboxAddress?.trim() ?? "";
  const provider = normalizeAgentMailProvider(agent.personalMailProvider);

  if (agent.personalMailStatus === "connected" && mailbox && provider) {
    return "connected";
  }
  if (agent.personalMailStatus === "pending" && provider) {
    return "pending";
  }
  if (agent.personalMailStatus === "disconnected") return "disconnected";

  // Legacy magic-click stub: personalMailConnected true without an explicit
  // connected status + mailbox must not look connected.
  return "disconnected";
}

export function agentPersonalMailIsConnected(agent: AgentPersonalMailFields): boolean {
  return resolvePersonalMailStatus(agent) === "connected";
}

export function agentPersonalMailIsPending(agent: AgentPersonalMailFields): boolean {
  return resolvePersonalMailStatus(agent) === "pending";
}

/**
 * Scrub Phase 1 magic-click stubs and normalize status/provider fields.
 * Keeps mailbox text if present, but never leaves a false "connected".
 */
export function migrateAgentPersonalMailFields<T extends AgentPersonalMailFields>(agent: T): T {
  const provider = normalizeAgentMailProvider(agent.personalMailProvider);
  const status = resolvePersonalMailStatus({
    ...agent,
    personalMailProvider: provider,
  });
  const mailbox = agent.mailboxAddress?.trim() ? agent.mailboxAddress.trim() : null;

  return {
    ...agent,
    personalMailProvider: status === "disconnected" ? null : provider,
    personalMailStatus: status,
    // Legacy boolean kept in sync for older readers; never invent connected.
    personalMailConnected: status === "connected",
    mailboxAddress: mailbox,
    mailboxSource: mailbox ? (agent.mailboxSource ?? null) : null,
  };
}

export function migrateAgentsPersonalMail<T extends AgentPersonalMailFields>(agents: T[]): T[] {
  return agents.map(migrateAgentPersonalMailFields);
}

/**
 * Customer-facing note when a provider is not connectable.
 * Never exposes secret names or wrangler instructions in the UI.
 */
export function providerUnavailableMessage(
  provider: AgentMailProvider,
  config: PersonalMailProviderConfig | null | undefined,
  options?: { configLoaded?: boolean },
): string | null {
  const option = agentMailProviderOption(provider);
  if (!option) return null;
  if (config?.ready) return null;

  const configLoaded = options?.configLoaded ?? true;

  // Session-bound providers (ATW Mail) need no OAuth client secrets.
  if (option.requiredSecrets.length === 0) {
    if (config && config.ready === false) {
      return config.note || option.nextStepNote;
    }
    return null;
  }

  if (!configLoaded) return null;

  if (config == null) {
    return option.nextStepNote;
  }
  if (config.ready === false) {
    return config.note || option.nextStepNote;
  }
  return config.note || option.nextStepNote;
}

/** @deprecated Prefer providerUnavailableMessage for customer UI. */
export function missingSecretsMessage(
  provider: AgentMailProvider,
  config: PersonalMailProviderConfig | null | undefined,
): string | null {
  return providerUnavailableMessage(provider, config);
}
